import { Box, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@work4all/data';
import { settings, useSetting } from '@work4all/data/lib/settings';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { InvoiceForm } from '@work4all/models/lib/Enums/InvoiceForm.enum';

import { DefaultIconButton } from '../../default-icon-button/DefaultIconButton';
import { ListEntityPicker } from '../components/list-entity-picker/ListEntityPicker';
import { IPickerProps } from '../types';

export const InvoiceFormTranslationKeys: Record<string, string> = {
  [InvoiceForm.ELEKTRONISCH]: 'INVOICE_FORM.ELEKTRONISCH',
  [InvoiceForm.PAPIER]: 'INVOICE_FORM.PAPIER',
};

export const INVOICE_FORM_TO_SETTING_VALUE: Record<InvoiceForm, number> = {
  [InvoiceForm.PAPIER]: 0,
  [InvoiceForm.ELEKTRONISCH]: 1,
};

export type IInvoiceFormPickerProps<TMultiple extends boolean> = IPickerProps<
  { id: string; name: string },
  TMultiple
>;

export function InvoiceFormPicker<TMultiple extends boolean>(
  props: IInvoiceFormPickerProps<TMultiple>
) {
  const { ...rest } = props;

  const { t } = useTranslation();
  const defaultInvoiceFormSetting = useSetting(settings.defaultInvoiceType());

  const dataSet = useMemo(() => {
    const taskStatusValues: { id: string; name: string }[] = [
      {
        id: InvoiceForm.ELEKTRONISCH,
        name: t(InvoiceFormTranslationKeys[InvoiceForm.ELEKTRONISCH]),
      },
      {
        id: InvoiceForm.PAPIER,
        name: t(InvoiceFormTranslationKeys[InvoiceForm.PAPIER]),
      },
    ];

    const defaultInvoiceForm = taskStatusValues.find(
      (status) =>
        INVOICE_FORM_TO_SETTING_VALUE[status.id] ===
        defaultInvoiceFormSetting.value
    );

    const dataWithoutDefaultInvoiceForm = taskStatusValues.filter(
      (status) =>
        INVOICE_FORM_TO_SETTING_VALUE[status.id] !==
        defaultInvoiceFormSetting.value
    );

    return {
      total: taskStatusValues.length,
      loading: false,
      fetchMore: () => Promise.resolve(),
      refetch: () => Promise.resolve(null),
      data: defaultInvoiceForm
        ? [defaultInvoiceForm, ...dataWithoutDefaultInvoiceForm]
        : dataWithoutDefaultInvoiceForm,
    };
  }, [defaultInvoiceFormSetting.value, t]);

  const user = useUser();
  const isAdmin = user.isMaster;

  const renderItemContent = useCallback(
    (status) => {
      const isDefault =
        INVOICE_FORM_TO_SETTING_VALUE[status.id] ===
        defaultInvoiceFormSetting.value;

      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          className="default-icon-button-parent"
        >
          <Typography variant="body2" noWrap>
            {status.name}
          </Typography>

          <DefaultIconButton
            isDefault={isDefault}
            disabled={!isAdmin}
            onClick={(e) => {
              e.stopPropagation();

              if (isDefault) {
                // Workaround for the fact that the default invoice form is not set
                defaultInvoiceFormSetting.set(-1);
                return;
              }

              defaultInvoiceFormSetting.set(
                INVOICE_FORM_TO_SETTING_VALUE[status.id]
              );
            }}
          />
        </Box>
      );
    },
    [defaultInvoiceFormSetting, isAdmin]
  );

  return (
    <ListEntityPicker
      entity={Entities.customer}
      multiple={false}
      maxItems={Math.max(dataSet.total, 3)}
      data={INVOICE_FORM_FIELDS}
      filterBy="name"
      sortBy="name"
      clearable={false}
      {...rest}
      fixedDataSet={dataSet}
      renderItemContent={renderItemContent}
    />
  );
}

const INVOICE_FORM_FIELDS = {
  id: null,
  name: null,
};
